<template>
  <VendorBlock
    class="vendor-about"
  >
    <template #title>
      {{ title }}
    </template>

    <template #content>
      <div class="vendor-about__wrapper">
        <div
          v-if="data.video_src"
          class="vendor-about__video-wrapper"
        >
          <iframe
            loading="lazy"
            :src="data.video_src"
            width="280"
            height="158"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture;"
            frameborder="0"
            allowfullscreen
            class="vendor-about__video"
          />
        </div>

        <div class="vendor-about__content">
          <div
            ref="textRef"
            class="vendor-about__text p p--2"
            v-html="data.content"
          />

          <UiShowMoreButton
            v-if="showMoreButton"
            size="big"
            has-icon
            :class="{'vendor-about__button--expanded': isExpanded}"
            class="vendor-about__button"
            @click="handleSpoiler"
          >
            {{ isExpanded ? 'Свернуть' : 'Читать полностью' }}
          </UiShowMoreButton>
        </div>
      </div>
    </template>
  </VendorBlock>
</template>

<script setup lang="ts">
  import UiShowMoreButton from '@academica-box/components/Buttons/UiShowMoreButton/UiShowMoreButton.vue'
  import VendorBlock from '@/components/Vendor/VendorBlock.vue'
  import type { VendorAbout } from '@/types/vendor'

  defineProps<{
    title: string;
    data: VendorAbout;
  }>()

  const viewport = useViewport()
  const isExpanded = ref(false)
  const showMoreButton = ref(false)
  const textRef = ref<HTMLDivElement | null>(null)

  const isMobile = computed(() => viewport.isLessThan('lg'))

  watch(isMobile, () => {
    setHeightToTextContainer()
  })

  onMounted(() => {
    setHeightToTextContainer()
  })

  const setHeightToTextContainer = () => {
    const textContainer = textRef.value as HTMLDivElement

    if (textContainer.childElementCount >= 2) {
      showMoreButton.value = true

      const firstParagraph = textContainer.querySelector<HTMLParagraphElement>('p:first-of-type')
      const firstParagraphHeight = firstParagraph?.offsetHeight

      textContainer.style.height = `${firstParagraphHeight}px`
    }
  }

  const handleSpoiler = () => {
    const textContainer = textRef.value as HTMLDivElement
    const firstParagraph = textContainer.querySelector<HTMLParagraphElement>('p:first-of-type')
    const firstParagraphHeight = firstParagraph?.offsetHeight

    if (!isExpanded.value) {
      textContainer.removeAttribute('style')
    } else {
      textContainer.style.height = `${firstParagraphHeight}px`
    }

    isExpanded.value = !isExpanded.value
  }
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;
  @use '@academica-box/utils/styles/mixins/UiMotion' as *;

  .vendor-about__video-wrapper {
    margin-bottom: 32px;

    @include media(sm-max) {
      position: relative;
      padding-bottom: calc(100% / 16 * 9);
    }

    @include media(md) {
      width: 696px;
      height: 394px;
    }

    @include media(lg) {
      width: 608px;
      height: 343px;
    }

    @include media(xl) {
      width: 672px;
      height: 379px;
    }

    @include media(xxl) {
      width: 848px;
      height: 478px;
    }
  }

  .vendor-about__video {
    width: 100%;
    height: 100%;
    border-radius: 16px;

    @include media(sm-max) {
      position: absolute;
      top: 0;
      left: 0;
    }

    @include media(lg) {
      border-radius: 20px;
    }
  }

  .vendor-about__subtitle {
    margin-bottom: 16px;
  }

  .vendor-about__text {
    overflow: hidden;
    color: var(--text-secondary-color);

    &:not(:last-child) {
      margin-bottom: 24px;
    }

    p {
      margin-bottom: 0;

      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }
  }

  .vendor-about__button .ab-ui-show-more-button__chevron {
    @include ui-motion(all icon-turn-main);
  }

  .vendor-about__button--expanded .ab-ui-show-more-button__chevron {
    transform: rotate(-180deg);
  }
</style>
